import { z } from 'zod';
import { frequencyArraySchema, generalOrdersShape, planOfCare485ValidationSchema, vitalsThresholdRefinement, VITALS_THRESHOLD_REFINEMENT_ISSUE, } from './v1';
const abilityLimitation = planOfCare485ValidationSchema.shape.data.shape.AbilityLimitation;
const hospitalization = planOfCare485ValidationSchema.shape.data.shape.Hospitalization;
const safety = planOfCare485ValidationSchema.shape.data.shape.Safety;
const nutritionAllergies = planOfCare485ValidationSchema.shape.data.shape.NutritionAllergies;
const disciplineOrders = planOfCare485ValidationSchema.shape.data.shape.DisciplineOrders;
const dataV2 = planOfCare485ValidationSchema.shape.data.extend({
    AbilityLimitation: abilityLimitation.omit({ activitiesPermittedOther: true }).extend({
        activitiesPermitted: z
            .object({
            weightBearingStatus: z.enum(['fwb', 'wbat', 'pwb', 'tdwb', 'nwb']).nullish(),
            weightBearingPercentage: z.string().nullish(),
            weightBearingLimb: z.enum(['rle', 'lle', 'rue', 'lue']).nullish(),
            activityLevel: z
                .enum(['complete-bedrest', 'bedrest-brp', 'transfer-only', 'up-as-tolerated', 'independent-at-home'])
                .nullish(),
            modifiers: z
                .array(z.enum([
                'assistive-device',
                'prescribed-exercises',
                'no-shower-tub',
                'hip-precautions',
                'sternal-precautions',
                'other',
            ]))
                .optional(),
            modifierOtherText: z.string().nullish(),
            modifierOtherDuration: z.string().nullish(),
        })
            .superRefine((ap, ctx) => {
            if (!ap.activityLevel) {
                ctx.addIssue({ path: ['activityLevel'], code: z.ZodIssueCode.custom, message: 'Activity level is required' });
            }
            if (ap.weightBearingStatus === 'pwb' && !ap.weightBearingPercentage) {
                ctx.addIssue({
                    path: ['weightBearingPercentage'],
                    code: z.ZodIssueCode.custom,
                    message: 'Percentage is required for Partial Weight Bearing',
                });
            }
            if (ap.weightBearingStatus === 'nwb' && !ap.weightBearingLimb) {
                ctx.addIssue({
                    path: ['weightBearingLimb'],
                    code: z.ZodIssueCode.custom,
                    message: 'Affected limb is required for Non-Weight Bearing',
                });
            }
            if (ap.modifiers?.includes('other') && !ap.modifierOtherText) {
                ctx.addIssue({
                    path: ['modifierOtherText'],
                    code: z.ZodIssueCode.custom,
                    message: 'Description is required for Other modifier',
                });
            }
        }),
        rehabPotential: z
            .object({
            returnToPriorFunction: z.enum(['excellent', 'good', 'fair', 'guarded', 'poor']).nullish(),
            remainAtHomeAlone: z.enum(['excellent', 'good', 'fair', 'guarded', 'poor']).nullish(),
            remainAtHomeWithCaregiver: z.enum(['excellent', 'good', 'fair', 'guarded', 'poor']).nullish(),
            jimmoJustification: z
                .object({
                skillNeeds: z
                    .array(z.enum(['maintain-function', 'manage-symptoms', 'educate-caregiver', 'monitor-complications', 'other']))
                    .optional(),
                narrative: z.string().nullish(),
            })
                .nullish(),
        })
            .superRefine((rp, ctx) => {
            if (!rp.returnToPriorFunction) {
                ctx.addIssue({ path: ['returnToPriorFunction'], code: z.ZodIssueCode.custom, message: 'required' });
            }
            if (!rp.remainAtHomeAlone) {
                ctx.addIssue({ path: ['remainAtHomeAlone'], code: z.ZodIssueCode.custom, message: 'required' });
            }
            if (!rp.remainAtHomeWithCaregiver) {
                ctx.addIssue({ path: ['remainAtHomeWithCaregiver'], code: z.ZodIssueCode.custom, message: 'required' });
            }
            const needsJimmo = [rp.returnToPriorFunction, rp.remainAtHomeAlone, rp.remainAtHomeWithCaregiver].some((v) => v === 'guarded' || v === 'poor');
            if (needsJimmo && !rp.jimmoJustification?.narrative) {
                ctx.addIssue({
                    path: ['jimmoJustification', 'narrative'],
                    code: z.ZodIssueCode.custom,
                    message: 'Skilled need justification narrative is required when rehab potential is Guarded or Poor',
                });
            }
        }),
        prognosis: z.enum(['excellent', 'good', 'fair', 'guarded', 'poor'], {
            message: 'required',
        }),
        functionalLimitation: z
            .array(z.enum([
            'ambulation',
            'amputation',
            'bladder incontinence',
            'bowel incontinence',
            'contracture',
            'decreased safety awareness',
            'dyspnea with minimal exertion',
            'endurance',
            'hearing',
            'legally blind',
            'pain limiting function',
            'paralysis',
            'speech',
            'transportation',
            'tremors',
            'vertigo',
            'other',
        ]))
            .optional(),
        // functionalLimitationOther inherited unchanged from v1.
    }),
    Hospitalization: hospitalization.extend({
        risks: z
            .array(z.enum([
            'fall risk',
            'recent hospitalization (within 60 days)',
            'multiple ED visits (2+ in past 6 months)',
            'unintentional weight loss (10+ lbs in past year)',
            'medication non-adherence / polypharmacy',
            'CHF exacerbation risk',
            'COPD exacerbation risk',
            'uncontrolled diabetes',
            'wound / skin breakdown risk',
            'caregiver deficit / lives alone',
            'cognitive impairment',
            'complex disease process',
            'coumadin dietary guidelines',
            'assistive device',
            'decreased safety awareness',
            'other',
        ]))
            .min(1, 'At least one risk factor is required'),
        necessaryInterventions: z
            .array(z.enum([
            'S/S of disease exacerbation and complications — notify physician',
            'S/S of infection / UTI — notify physician',
            'emergency medical measures — when to call 911',
            'medication regimen education — issues to report and who to contact',
            'pain management education',
            'handwashing / standard precautions',
            'identify and address patient-specific condition risk factors',
            'report changes in condition to responsible practitioner / physician',
            'home safety and emergency plan reviewed',
            'natural disaster preparedness',
            'home fire preparedness',
            'power outage / equipment failure plan',
            'evacuation or shelter-in-place plan',
        ]))
            .min(1, 'At least one intervention is required'),
        // risksOther inherited unchanged from v1.
    }),
    Safety: safety
        .extend({
        safetyMeasures: z
            .array(z.enum([
            '24hr Supervision',
            'ambulate with assistance/supervision (specify where)',
            'avoid constrictive clothing',
            'avoid respiratory irritants',
            'avoid smoking unattended',
            'avoid temperature extremes',
            'carry a cordless phone',
            'anticoagulant',
            'o2 precautions',
            'change position slowly',
            'clear walking areas',
            'dispose of dressings properly',
            'dispose of sharps properly',
            'do not raise affected arm above shoulder level or behind back for 2-3 weeks/until physician oks',
            'fall precaution',
            'hip precautions for',
            'posterior lateral',
            'anterior approach',
            'install/change batteries in smoke detector',
            'joint dislocation precautions',
            'make emergency plan',
            'minimize/reduce environmental hazards',
            'presence of animal',
            'prone to skin breakdown Precautions',
            'protect from wandering',
            'seizure precautions',
            'store dressings/supplies and equipment safely',
            'supervise medication administration',
            'transfer with assistance',
            'use adequate lighting and night light',
            'use assistive device with mobility',
            'use banister/rails with steps',
            'use/install grab bars in bathroom',
            'wear non-skid footwear',
        ]))
            .optional(),
        // ambulateLocation and animalDescription inherited unchanged from v1.
    })
        .superRefine((data, ctx) => {
        if (data.safetyMeasures?.includes('ambulate with assistance/supervision (specify where)') && !data.ambulateLocation) {
            ctx.addIssue({ path: ['ambulateLocation'], code: z.ZodIssueCode.custom, message: 'Required' });
        }
        if (data.safetyMeasures?.includes('presence of animal') && !data.animalDescription) {
            ctx.addIssue({ path: ['animalDescription'], code: z.ZodIssueCode.custom, message: 'Required' });
        }
    }),
    NutritionAllergies: nutritionAllergies
        .omit({ nutritionalRequirements: true, nutritionalRequirementsOther: true, tubeFeeding: true, tubeFeedingFreq: true })
        .extend({
        dietTexture: z.enum([
            'regular',
            'mechanically-altered-chopped',
            'mechanically-altered-puree',
            'thickened-liquids',
            'full-liquid',
            'clear-liquid',
            'npo',
        ], { message: 'Diet texture is required' }),
        npoReason: z.string().nullish(),
        thickenedLiquidsLevel: z.enum(['slightly-thick', 'mildly-thick', 'moderately-thick', 'extremely-thick']).nullish(),
        therapeuticDiet: z
            .array(z.enum([
            'diabetic',
            'cardiac-low-sodium',
            'dash',
            'renal',
            'low-cholesterol',
            'coumadin-vitamin-k',
            'gluten-free',
            'lactose-restricted',
            'high-fiber',
            'other',
        ]))
            .optional(),
        therapeuticDietOther: z.string().nullish(),
        fluidManagement: z.enum(['no-restriction', 'fluid-restriction', 'encourage-fluids'], {
            message: 'Fluid management is required',
        }),
        fluidVolumeML: z.string().nullish(),
        tubeFeedingType: z.enum(['ng-tube', 'g-tube', 'j-tube', 'peg-j', 'nj-tube']).nullish(),
        tubeFeedingFormula: z.string().nullish(),
        tubeFeedingRate: z.string().nullish(),
        tubeFeedingSchedule: z.string().nullish(),
        supplements: z.array(z.enum(['ensure', 'boost', 'glucerna', 'nepro', 'promod', 'other'])).optional(),
        supplementsOther: z.string().nullish(),
        nka: z.boolean().optional(),
        // allergies inherited unchanged from v1.
    })
        .superRefine((data, ctx) => {
        if (data.dietTexture === 'npo' && !data.npoReason) {
            ctx.addIssue({ path: ['npoReason'], code: z.ZodIssueCode.custom, message: 'Clinical indication required for NPO' });
        }
        if (data.dietTexture === 'thickened-liquids' && !data.thickenedLiquidsLevel) {
            ctx.addIssue({
                path: ['thickenedLiquidsLevel'],
                code: z.ZodIssueCode.custom,
                message: 'IDDSI level required for thickened liquids',
            });
        }
        if ((data.fluidManagement === 'fluid-restriction' || data.fluidManagement === 'encourage-fluids') && !data.fluidVolumeML) {
            ctx.addIssue({ path: ['fluidVolumeML'], code: z.ZodIssueCode.custom, message: 'Volume (mL/day) required' });
        }
        if (!data.nka && !data.allergies?.some((a) => a.allergy.length > 0)) {
            ctx.addIssue({
                path: ['nka'],
                code: z.ZodIssueCode.custom,
                message: 'Confirm "No Known Allergies" or document at least one allergy',
            });
        }
    }),
    DisciplineOrders: disciplineOrders.omit({ oxygenOrders: true }).extend({
        sn: z
            .object({
            service: z.string().optional(),
            frequency: frequencyArraySchema,
            prnVisits: z.string().optional(),
        })
            .refine((data) => data.service || data.frequency.length || data.prnVisits, {
            message: 'Please fill in SN service information',
            path: [],
        }),
        ha: z
            .object({
            service: z.string().optional(),
            frequency: frequencyArraySchema,
        })
            .refine((data) => data.service || data.frequency.length, {
            message: 'Please fill in HHA service information',
            path: [],
        }),
        dmeOrders: z
            .array(z.object({
            item: z.string().min(1, 'Equipment item required'),
            hcpcs: z.string().nullish(),
            supplier: z.string().nullish(),
            deliveryStatus: z.enum(['ordered', 'pending', 'delivered', 'backordered']).nullish(),
            quantity: z.string().nullish(),
        }))
            .optional(),
        oxygenOrder: z
            .object({
            flowRate: z.string().nullish(),
            route: z.enum(['nasal-cannula', 'simple-mask', 'non-rebreather', 'venturi-mask', 'transtracheal', 'high-flow']).nullish(),
            schedule: z.string().nullish(),
            spO2Target: z.string().nullish(),
            prnTrigger: z.array(z.string()).optional(),
            prnTriggerSpO2: z.string().nullish(),
            prnTriggerOther: z.string().nullish(),
            backupSource: z.enum(['concentrator-tanks', 'portable-only', 'liquid-o2', 'concentrator-battery']).nullish(),
            qualifyingSpO2: z.string().nullish(),
            qualifyingDate: z.string().nullish(),
            qualifyingCondition: z.string().nullish(),
            abgPaO2: z.string().nullish(),
            group2Condition: z
                .enum(['chf-dependent-edema', 'pulmonary-hypertension-cor-pulmonale', 'erythrocythemia-hct-56'])
                .nullish(),
            qualifyingTestByPhysician: z.boolean().nullish(),
            educationCompleted: z
                .array(z.enum([
                'fire-safety',
                'equipment-operation',
                'tubing-care',
                'emergency-procedures',
                'travel-use',
                'backup-contact',
            ]))
                .optional(),
        })
            .nullish(),
        // msw, pt, ot, slp, advanceDirectives, patientCopyProvided inherited unchanged from v1.
    }),
    GeneralOrders: generalOrdersShape
        .omit({ order: true })
        .extend({
        anticipatedDischargeSetting: z
            .enum([
            'home-independent',
            'home-family-caregiver-support',
            'home-community-services',
            'assisted-living-facility',
            'skilled-nursing-facility',
            'hospice',
            'other',
        ])
            .nullish(),
        barrierToDischarge: z
            .object({
            noneIdentified: z.boolean().optional(),
            barriers: z
                .array(z.object({
                category: z.enum([
                    'physical-functional',
                    'cognitive',
                    'medical-complexity',
                    'caregiver',
                    'home-environment',
                    'financial',
                    'equipment-dme',
                    'non-adherence',
                    'behavioral-psychosocial',
                ]),
                qualifier: z.string().min(1, 'Qualifier required'),
            }))
                .optional(),
            evidenceChipsShown: z.array(z.string()).optional(),
        })
            .nullish(),
        // parameters and dischargePlanningDate inherited unchanged from v1.
    })
        .refine(vitalsThresholdRefinement, VITALS_THRESHOLD_REFINEMENT_ISSUE),
});
const metaV2 = z
    .object({
    // Optional: the server may return a meta object without this key — its absence must not block submission.
    dismissedSuggestions: z
        .object({
        functionalLimitation: z.array(z.string()).optional(),
        activitiesModifiers: z.array(z.string()).optional(),
        activityLevel: z.boolean().optional(),
        rehabReturnToPriorFunction: z.boolean().optional(),
        rehabRemainAtHomeAlone: z.boolean().optional(),
        rehabRemainAtHomeWithCaregiver: z.boolean().optional(),
        prognosis: z.boolean().optional(),
        safetyMeasures: z.array(z.string()).optional(),
        dietTexture: z.boolean().optional(),
        therapeuticDiet: z.array(z.string()).optional(),
        supplements: z.array(z.string()).optional(),
        fluidManagement: z.boolean().optional(),
        fluidVolumeML: z.boolean().optional(),
        hospitalizationRisks: z.array(z.string()).optional(),
        tubeFeedingIndication: z.boolean().optional(),
        // Apply-target kept distinct from tubeFeedingIndication (the dismiss flag)
        // so useSingleSelectSuggestion never writes both to the same path.
        tubeFeedingIndicationApplied: z.boolean().optional(),
        tubeFeedingType: z.boolean().optional(),
        tubeFeedingFormula: z.boolean().optional(),
        tubeFeedingRate: z.boolean().optional(),
        tubeFeedingSchedule: z.boolean().optional(),
        adLivingWill: z.boolean().optional(),
        adDnr: z.boolean().optional(),
        adSurrogate: z.boolean().optional(),
        adCopyObtained: z.boolean().optional(),
        oxygenFlowRate: z.boolean().optional(),
        oxygenRoute: z.boolean().optional(),
        oxygenSchedule: z.boolean().optional(),
        oxygenQualifyingSpO2: z.boolean().optional(),
        oxygenQualifyingDate: z.boolean().optional(),
        mentalStatus: z.array(z.string()).optional(),
        mentalStatusAlertness: z.boolean().optional(),
        mentalStatusOrientedToPerson: z.boolean().optional(),
        mentalStatusOrientedToPlace: z.boolean().optional(),
        mentalStatusOrientedToPurpose: z.boolean().optional(),
        mentalStatusOrientedToTime: z.boolean().optional(),
    })
        .optional(),
})
    .nullish();
export const planOfCare485ValidationSchemaV2 = planOfCare485ValidationSchema.extend({
    data: dataV2,
    meta: metaV2,
});
